import { Link, useParams } from 'react-router-dom';
import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useLanguageLink } from '../../hooks/useLanguageLink';
import SEO from '../../components/seo/SEO';
import SchemaMarkup from '../../components/seo/SchemaMarkup';
import blogService from '../../services/blog';
import type { BlogPost } from '../../types/blog';
import api from '../../services/api';

const ALL_POSTS_KEY = '__all_posts__'; // Internal key for "All Posts" category

export default function BlogListPage() {
  const { t } = useTranslation('blog');
  const { createLink } = useLanguageLink();
  const { lang } = useParams<{ lang: string }>();
  const currentLang = lang || 'en';
  const [selectedCategory, setSelectedCategory] = useState<string>(ALL_POSTS_KEY);
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterStatus, setNewsletterStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [newsletterMessage, setNewsletterMessage] = useState('');

  // Fetch blog posts from API
  const { data: blogPosts = [], isLoading, error } = useQuery({
    queryKey: ['public-blog-posts', currentLang],
    queryFn: () => blogService.getPublicBlogPosts(currentLang),
  });

  // Get unique categories from blog posts
  const categories = useMemo(() => {
    const uniqueCategories = Array.from(new Set(blogPosts.map(post => post.category)));
    return [ALL_POSTS_KEY, ...uniqueCategories];
  }, [blogPosts]);

  // Filter posts based on selected category
  const filteredPosts = useMemo(() => {
    if (selectedCategory === ALL_POSTS_KEY) {
      return blogPosts;
    }
    return blogPosts.filter(post => post.category === selectedCategory);
  }, [selectedCategory, blogPosts]);

  // Get post count for each category
  const getCategoryCount = (category: string) => {
    if (category === ALL_POSTS_KEY) {
      return blogPosts.length;
    }
    return blogPosts.filter(post => post.category === category).length;
  };

  // Handle newsletter subscription
  const handleNewsletterSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!newsletterEmail) return;

    setNewsletterStatus('loading');

    try {
      const response = await api.post('/api/newsletter/subscribe', {
        email: newsletterEmail,
        source: 'blog',
      });

      setNewsletterStatus('success');
      setNewsletterMessage(response.data.message);
      setNewsletterEmail('');

      // Reset after 5 seconds
      setTimeout(() => {
        setNewsletterStatus('idle');
        setNewsletterMessage('');
      }, 5000);
    } catch (error: any) {
      setNewsletterStatus('error');
      setNewsletterMessage(error.response?.data?.message || 'Failed to subscribe. Please try again.');

      // Reset after 5 seconds
      setTimeout(() => {
        setNewsletterStatus('idle');
        setNewsletterMessage('');
      }, 5000);
    }
  };

  return (
    <>
      <SEO
        title={t('seo.title')}
        description={t('seo.description')}
        canonical="/blog"
        keywords={t('seo.keywords')}
      />

      <SchemaMarkup
        type="website"
        name={t('schema.name')}
        description={t('schema.description')}
      />

      <div className="min-h-screen">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-slate-50 via-blue-50 to-primary-100 py-20 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-primary-200 rounded-full mix-blend-multiply filter blur-xl opacity-30" />
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-blue-200 rounded-full mix-blend-multiply filter blur-xl opacity-30" />
          <div className="container-marketing max-w-6xl relative">
            <div className="text-center">
              <h1 className="text-5xl md:text-6xl font-bold mb-6">
                <span className="bg-clip-text text-transparent bg-gradient-to-r from-primary-600 to-primary-800">
                  {t('hero.title')}
                </span>
              </h1>
              <p className="text-xl md:text-2xl text-gray-600 max-w-3xl mx-auto">
                {t('hero.subtitle')}
              </p>
            </div>
          </div>
        </section>

        {/* Blog Posts Grid */}
        <section className="py-20 bg-white">
          <div className="container-marketing max-w-6xl">
            {/* Loading state */}
            {isLoading && (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
                <p className="text-lg text-gray-600 mt-4">{t('loading')}</p>
              </div>
            )}

            {/* Error state */}
            {error && (
              <div className="text-center py-12">
                <p className="text-xl text-red-600">{t('error')}</p>
              </div>
            )}

            {/* Show selected category */}
            {!isLoading && !error && selectedCategory !== ALL_POSTS_KEY && (
              <div className="mb-8 text-center">
                <p className="text-lg text-gray-600">
                  {t('showing')} <span className="font-semibold text-primary-600">{filteredPosts.length}</span> {filteredPosts.length !== 1 ? t('posts_plural') : t('posts')} {t('in')} <span className="font-semibold">{selectedCategory}</span>
                </p>
              </div>
            )}

            {!isLoading && !error && filteredPosts.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-xl text-gray-600">{t('no_posts')}</p>
              </div>
            ) : !isLoading && !error && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {filteredPosts.map((post) => {
                  // Handle slugs that contain paths (e.g., "vs/adspect")
                  const postPath = post.slug.includes('/') ? post.slug : `blog/${post.slug}`;

                  return (
                  <Link
                    key={post.slug}
                    to={createLink(postPath)}
                    className="group bg-white rounded-2xl overflow-hidden border-2 border-gray-200 hover:border-primary-300 transition-all duration-300 hover:shadow-xl"
                  >
                    {/* Featured Image */}
                    {post.image_url ? (
                      <div className="aspect-video bg-gradient-to-br from-primary-100 to-purple-100 overflow-hidden">
                        <img
                          src={post.image_url}
                          alt={post.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          onError={(e) => {
                            // Fallback to gradient if image fails to load
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                      </div>
                    ) : (
                      <div className="aspect-video bg-gradient-to-br from-primary-100 to-purple-100 flex items-center justify-center">
                        <svg className="w-16 h-16 text-primary-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
                        </svg>
                      </div>
                    )}

                    {/* Post Content */}
                    <div className="p-6">
                      {/* Category & Read Time */}
                      <div className="flex items-center gap-3 mb-3">
                        <span className="inline-block px-3 py-1 bg-primary-100 text-primary-700 rounded-full text-xs font-semibold">
                          {post.category}
                        </span>
                        <span className="text-xs text-gray-500">{post.read_time} {t('read_time')}</span>
                      </div>

                      {/* Title */}
                      <h2 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-primary-600 transition-colors line-clamp-2">
                        {post.title}
                      </h2>

                      {/* Excerpt */}
                      <p className="text-gray-600 mb-4 line-clamp-3">
                        {post.excerpt}
                      </p>

                      {/* Meta */}
                      <div className="flex items-center justify-between text-sm text-gray-500">
                        <span>{post.published_at ? new Date(post.published_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'Draft'}</span>
                        <span className="flex items-center gap-1 text-primary-600 font-semibold group-hover:gap-2 transition-all">
                          {t('read_more')}
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </span>
                      </div>
                    </div>
                  </Link>
                  );
                })}
              </div>
            )}
          </div>
        </section>

        {/* Categories Section */}
        <section className="py-16 bg-gray-50">
          <div className="container-marketing max-w-6xl">
            <h2 className="text-3xl font-bold text-center mb-8">{t('categories.title')}</h2>
            <div className="flex flex-wrap justify-center gap-4">
              {categories.map((category) => {
                const count = getCategoryCount(category);
                const isActive = selectedCategory === category;

                return (
                  <button
                    key={category}
                    onClick={() => setSelectedCategory(category)}
                    className={`px-6 py-3 rounded-lg font-semibold transition-all duration-200 ${
                      isActive
                        ? 'bg-primary-600 text-white shadow-lg scale-105'
                        : 'bg-white text-gray-700 border-2 border-gray-200 hover:border-primary-300 hover:bg-primary-50'
                    }`}
                  >
                    {category === ALL_POSTS_KEY ? t('categories.all_posts') : category}
                    <span className={`ml-2 text-xs ${isActive ? 'text-purple-200' : 'text-gray-500'}`}>
                      ({count})
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </section>

        {/* Newsletter CTA - Redesigned */}
        <section className="py-20 bg-gradient-to-br from-gray-50 to-purple-50">
          <div className="container-marketing max-w-5xl">
            <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
              <div className="grid md:grid-cols-2 gap-0">
                {/* Left side - Info */}
                <div className="bg-gradient-to-br from-primary-600 to-purple-700 p-8 md:p-12 text-white flex flex-col justify-center">
                  <div className="mb-6">
                    <svg className="w-16 h-16 text-purple-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 19v-8.93a2 2 0 01.89-1.664l7-4.666a2 2 0 012.22 0l7 4.666A2 2 0 0121 10.07V19M3 19a2 2 0 002 2h14a2 2 0 002-2M3 19l6.75-4.5M21 19l-6.75-4.5M3 10l6.75 4.5M21 10l-6.75 4.5m0 0l-1.14.76a2 2 0 01-2.22 0l-1.14-.76" />
                    </svg>
                  </div>
                  <h2 className="text-3xl md:text-4xl font-bold mb-4">
                    {t('newsletter.title')}
                  </h2>
                  <p className="text-lg text-purple-100 mb-6">
                    {t('newsletter.subtitle')}
                  </p>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <svg className="w-6 h-6 text-green-300 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span className="text-purple-100">{t('newsletter.benefits.weekly')}</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <svg className="w-6 h-6 text-green-300 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span className="text-purple-100">{t('newsletter.benefits.exclusive')}</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <svg className="w-6 h-6 text-green-300 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span className="text-purple-100">{t('newsletter.benefits.early_access')}</span>
                    </li>
                  </ul>
                </div>

                {/* Right side - Form */}
                <div className="p-8 md:p-12 flex flex-col justify-center bg-white">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">
                    {t('newsletter.form.title')}
                  </h3>
                  <p className="text-gray-600 mb-6">
                    {t('newsletter.form.subtitle')}
                  </p>

                  {newsletterStatus === 'success' && (
                    <div className="mb-6 p-4 bg-green-50 border-l-4 border-green-500 rounded-lg">
                      <div className="flex items-center gap-3">
                        <svg className="w-6 h-6 text-green-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <p className="text-green-800 font-medium">{newsletterMessage}</p>
                      </div>
                    </div>
                  )}

                  {newsletterStatus === 'error' && (
                    <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 rounded-lg">
                      <div className="flex items-center gap-3">
                        <svg className="w-6 h-6 text-red-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                        </svg>
                        <p className="text-red-800 font-medium">{newsletterMessage}</p>
                      </div>
                    </div>
                  )}

                  <form onSubmit={handleNewsletterSubscribe} className="space-y-4">
                    <div>
                      <label htmlFor="newsletter-email" className="sr-only">
                        Email address
                      </label>
                      <input
                        type="email"
                        id="newsletter-email"
                        value={newsletterEmail}
                        onChange={(e) => setNewsletterEmail(e.target.value)}
                        placeholder={t('newsletter.form.placeholder')}
                        required
                        disabled={newsletterStatus === 'loading'}
                        className="w-full px-6 py-4 border-2 border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-200 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={newsletterStatus === 'loading'}
                      className="w-full px-8 py-4 bg-gradient-to-r from-primary-600 to-purple-600 text-white rounded-lg font-semibold hover:from-primary-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transform hover:scale-[1.02] transition-all duration-200 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                    >
                      {newsletterStatus === 'loading' ? (
                        <span className="flex items-center justify-center gap-2">
                          <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          {t('newsletter.form.submitting')}
                        </span>
                      ) : (
                        <span className="flex items-center justify-center gap-2">
                          {t('newsletter.form.submit')}
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                          </svg>
                        </span>
                      )}
                    </button>
                  </form>

                  <p className="text-sm text-gray-500 mt-4 text-center">
                    🔒 {t('newsletter.form.privacy')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
